import { Box, BoxProps, Stack, Typography } from "@mui/material";
import { ReactNode, useState } from "react";

import FullScreenFileViewerV2 from "@/common/components/Images/FullScreenFileViewerV2";
import { FileItem } from "@/common/fileItem";

import ImageComparisonLabels from "./ImageComparisonLabels";
import ImageComparisonSlider from "./ImageComparisonSlider";
import ImageComparisonToolbar from "./ImageComparisonToolbar";

export interface ImageComparisonCenterStageProps {
  before: FileItem;
  after: FileItem;
  beforeLabel?: ReactNode;
  afterLabel?: ReactNode;
  isShowLabels: boolean;
  isSwapped: boolean;
  sx?: BoxProps["sx"];
  onToggleLabels: () => void;
  onToggleSwap: () => void;
}

export default function ImageComparisonCenterStage({
  before,
  after,
  beforeLabel,
  afterLabel,
  isShowLabels,
  isSwapped,
  sx,
  onToggleLabels,
  onToggleSwap,
}: ImageComparisonCenterStageProps) {
  const [resetSliderPositionKey, setResetSliderPositionKey] = useState(0);
  const [fsFiles, setFsFiles] = useState<FileItem[]>([]);
  const [fsSelectedId, setFsSelectedId] = useState<string | undefined>(undefined);

  const beforeForDisplay = isSwapped ? after : before;
  const afterForDisplay = isSwapped ? before : after;
  const beforeLabelForDisplay = isSwapped ? afterLabel : beforeLabel;
  const afterLabelForDisplay = isSwapped ? beforeLabel : afterLabel;

  return (
    <Stack spacing={1} sx={{ width: "100%", height: "100%", minWidth: 0, ...sx }}>
      <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
        <Typography variant='subtitle1' fontWeight={700}>
          Comparison
        </Typography>
        <ImageComparisonToolbar
          isShowLabels={isShowLabels}
          onToggleLabels={onToggleLabels}
          onResetSlider={() => setResetSliderPositionKey((k) => k + 1)}
          onSwapSides={onToggleSwap}
          onOpenBeforeFullscreen={() => {
            if (!beforeForDisplay) return;
            setFsFiles([beforeForDisplay]);
            setFsSelectedId(beforeForDisplay.id);
          }}
          onOpenAfterFullscreen={() => {
            if (!afterForDisplay) return;
            setFsFiles([afterForDisplay]);
            setFsSelectedId(afterForDisplay.id);
          }}
        />
      </Stack>

      <Box
        sx={{
          position: "relative",
          width: "100%",
          flex: 1,
          minHeight: 240,
          borderRadius: 2,
          overflow: "hidden",
          // aspectRatio: "4 / 3",
          // border: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        <ImageComparisonSlider
          leftImage={beforeForDisplay}
          rightImage={afterForDisplay}
          resetSliderPositionKey={resetSliderPositionKey}
        />

        <ImageComparisonLabels
          isVisible={isShowLabels}
          beforeLabel={beforeLabelForDisplay}
          afterLabel={afterLabelForDisplay}
        />
      </Box>

      {/* Fullscreen viewer (reuses existing zoom/pan UX) */}
      <FullScreenFileViewerV2
        files={fsFiles}
        selectedFileId={fsSelectedId}
        onSelectFile={(file) => setFsSelectedId(file.id)}
        onClose={() => {
          setFsFiles([]);
          setFsSelectedId(undefined);
        }}
      />
    </Stack>
  );
}
